var ArticleToc = class extends HTMLElement {
  constructor() {
    super();
    this._entries = [];
    this._currentIndex = 0;
    this._scrollHandler = null;
    this._resizeHandler = null;
    this._isProgrammaticScrolling = false;
    this._scrollEndTimer = null;
    this._mobileDirectory = null;
  }

  connectedCallback() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => requestAnimationFrame(() => this._init()), { once: true });
    } else {
      requestAnimationFrame(() => this._init());
    }
  }

  disconnectedCallback() {
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    if (this._scrollEndTimer) {
      window.clearTimeout(this._scrollEndTimer);
    }
  }

  _init() {
    const headingClass = this.dataset.headingClass || "toc-heading";
    const tocList = this.querySelector(".directory-items");

    if (!tocList) return;

    // 将静态 #introduction 项作为第一个条目
    const staticItem = tocList.querySelector(".article-directory-item");
    const introEl = this.querySelector("#introduction");
    if (staticItem && introEl) {
      this._entries.push({ tocItem: staticItem, h2Elements: [introEl] });
    }

    // 只在 .article-content-inner 内查找 H2；无指定类名则降级为所有 H2
    const contentInner = this.querySelector(".article-content-inner");
    if (!contentInner) return;

    let h2List = contentInner.querySelectorAll("h2." + headingClass);
    if (h2List.length === 0) {
      h2List = contentInner.querySelectorAll("h2");
    }
    if (h2List.length > 0) {
      this._buildToc(h2List, tocList);
    }

    if (this._entries.length === 0) return;

    this._setupClickListeners();
    this._setupScrollListener();
    this._setupResizeListener();
    this._handleResize();
  }

  // 根据 H2 列表构建目录项，相同文本合并为同一条目
  _buildToc(h2List, tocList) {
    const textToEntry = new Map();

    h2List.forEach((h2, index) => {
      const text = h2.textContent.trim();
      const id = "toc-h2-" + index;
      h2.setAttribute("id", id);

      if (textToEntry.has(text)) {
        textToEntry.get(text).h2Elements.push(h2);
        return;
      }

      const item = document.createElement("div");
      item.classList.add("article-directory-item");
      const a = document.createElement("a");
      a.href = "#" + id;
      a.textContent = text;
      item.appendChild(a);
      tocList.appendChild(item);

      const entry = { tocItem: item, h2Elements: [h2] };
      textToEntry.set(text, entry);
      this._entries.push(entry);
    });
  }

  _setupClickListeners() {
    this._entries.forEach((entry, index) => {
      entry.tocItem.addEventListener("click", (e) => {
        e.preventDefault();
        this._currentIndex = index;
        this._updateActive(index);
        this._scrollToElement(entry.h2Elements[0]);
      });
    });
  }

  // 参考 StickyNav._setupScrollListener
  _setupScrollListener() {
    this._scrollHandler = this._throttle(() => {
      if (!this._isProgrammaticScrolling) {
        this._updateActiveByScrollPosition();
      }
    }, 16);
    window.addEventListener("scroll", this._scrollHandler, { passive: true });

    // 图片/字体加载完成后再做首次位置计算
    if (document.readyState === "complete") {
      this._updateActiveByScrollPosition();
    } else {
      window.addEventListener("load", () => this._updateActiveByScrollPosition(), { once: true });
    }
  }

  // 动态监听窗口宽度，<768 时注入移动端目录，>=768 时移除
  _setupResizeListener() {
    this._resizeHandler = this._throttle(() => this._handleResize(), 100);
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  _handleResize() {
    if (window.innerWidth < 768) {
      this._injectMobileDirectory();
    } else {
      this._removeMobileDirectory();
    }
  }

  // 克隆 .article-directory 追加到 article-toc 末尾（移动端）
  _injectMobileDirectory() {
    if (this._mobileDirectory) return;

    const originalDir = this.querySelector(".article-directory");
    if (!originalDir) return;

    const clone = originalDir.cloneNode(true);
    clone.classList.add("article-directory--mobile");
    this.appendChild(clone);
    this._mobileDirectory = clone;

    // 为克隆项绑定点击事件
    const cloneItems = clone.querySelectorAll(".article-directory-item");
    cloneItems.forEach((item, i) => {
      if (i >= this._entries.length) return;
      item.addEventListener("click", (e) => {
        e.preventDefault();
        this._currentIndex = i;
        this._updateActive(i);
        this._scrollToElement(this._entries[i].h2Elements[0]);
      });
    });

    this._syncMobileActive();
  }

  _removeMobileDirectory() {
    if (!this._mobileDirectory) return;
    this._mobileDirectory.remove();
    this._mobileDirectory = null;
  }

  // 同步移动端目录的激活状态
  _syncMobileActive() {
    if (!this._mobileDirectory) return;
    const cloneItems = this._mobileDirectory.querySelectorAll(".article-directory-item");
    cloneItems.forEach((item, i) => {
      item.classList.toggle("active", i === this._currentIndex);
    });
  }

  // 激活最接近视口中心线的 H2 对应目录项
  _updateActiveByScrollPosition() {
    const viewportCenter = window.pageYOffset + window.innerHeight / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    this._entries.forEach((entry, index) => {
      entry.h2Elements.forEach((h2) => {
        const top = h2.getBoundingClientRect().top + window.pageYOffset;
        const distance = Math.abs(top - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
    });

    if (closestIndex !== this._currentIndex) {
      this._currentIndex = closestIndex;
      this._updateActive(closestIndex);
    }
  }

  // 同时更新原始目录和移动端克隆目录的激活状态
  _updateActive(index) {
    this._entries.forEach((entry, i) => {
      entry.tocItem.classList.toggle("active", i === index);
    });
    this._syncMobileActive();
  }

  // 参考 StickyNav._scrollToTarget
  _scrollToElement(el) {
    const elRect = el.getBoundingClientRect();
    const elCenter = elRect.top + elRect.height / 2;
    const targetTop = window.pageYOffset + elCenter - window.innerHeight / 2;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
    this._isProgrammaticScrolling = true;
    this._scheduleProgrammaticScrollUnlock();
  }

  // 参考 StickyNav._scheduleProgrammaticScrollUnlock
  _scheduleProgrammaticScrollUnlock() {
    if (this._scrollEndTimer) window.clearTimeout(this._scrollEndTimer);
    this._scrollEndTimer = window.setTimeout(() => {
      this._isProgrammaticScrolling = false;
      this._scrollEndTimer = null;
      this._updateActiveByScrollPosition();
    }, 160);
  }

  _getHeaderHeight() {
    const header = document.querySelector(".header-container");
    return header ? header.getBoundingClientRect().height : 0;
  }

  // 参考 StickyNav._throttle
  _throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
};

if (!window.customElements.get("article-toc")) {
  window.customElements.define("article-toc", ArticleToc);
}
